import {
  useMemo,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { cn } from '@ai-tutor/ui';
import { formatClock } from './PodcastPill';

/** Bars drawn: enough to read as speech, few enough to stay chunky. */
const BARS = 64;
/** Height of a bar with no sound under it, so silence still reads as a bar. */
const FLOOR = 0.12;
/** Arrow-key step, in seconds. */
const STEP = 5;

/**
 * `peaks` (0-1, precomputed with the podcast) reduced to `count` bars by the
 * loudest value in each stretch. A podcast without peaks gets a gentle even
 * shape instead of a blank track.
 */
export function barHeights(peaks: number[], count = BARS): number[] {
  if (!peaks.length) {
    return Array.from({ length: count }, (_, i) =>
      Math.max(FLOOR, 0.35 + 0.15 * Math.sin(i * 0.9))
    );
  }
  const loudest = Math.max(...peaks) || 1;
  return Array.from({ length: count }, (_, i) => {
    const from = Math.floor((i * peaks.length) / count);
    const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / count));
    const peak = Math.max(...peaks.slice(from, to));
    return Math.max(FLOOR, peak / loudest);
  });
}

/**
 * The podcast's waveform as a row of bars that doubles as its seek control:
 * the part already heard is filled in, a click or drag moves the audio, and
 * the arrow keys step it (it is a `slider` to assistive tech, with the time
 * as its value). Drawn from the precomputed peaks, so there is nothing to
 * download or decode.
 */
export function PodcastWave({
  peaks,
  currentTime,
  duration,
  onSeek,
  className,
}: {
  peaks: number[];
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bars = useMemo(() => barHeights(peaks), [peaks]);
  const length = Math.max(1, duration);
  const played = Math.min(1, Math.max(0, currentTime / length));

  const handlers = useWaveSeek(ref, currentTime, length, onSeek);

  return (
    <div
      ref={ref}
      role='slider'
      tabIndex={0}
      aria-label='Position'
      aria-valuemin={0}
      aria-valuemax={Math.floor(length)}
      aria-valuenow={Math.floor(Math.min(currentTime, length))}
      aria-valuetext={`${formatClock(currentTime)} of ${formatClock(duration)}`}
      {...handlers}
      // 44px tall: a finger's worth on a phone. `touch-none` so a drag
      // across it seeks rather than scrolling the lesson.
      className={cn(
        'group flex h-11 w-full min-w-0 cursor-pointer touch-none select-none items-center gap-px overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:gap-[2px]',
        className
      )}
    >
      {bars.map((h, i) => (
        <span
          key={i}
          aria-hidden='true'
          className={cn(
            'min-w-0 flex-1 rounded-full motion-safe:transition-colors motion-safe:duration-150',
            (i + 0.5) / bars.length <= played
              ? 'bg-primary'
              : 'bg-foreground/15 group-hover:bg-foreground/25'
          )}
          // The loudest bar is 80% of the row, so the row keeps some air.
          style={{ height: `${Math.round(h * 80)}%` }}
        />
      ))}
    </div>
  );
}

/** Click or drag to a point, or step with the keys (Home / End to the ends). */
function useWaveSeek(
  ref: RefObject<HTMLDivElement>,
  currentTime: number,
  length: number,
  onSeek: (time: number) => void
) {
  const dragging = useRef(false);
  const seekTo = (clientX: number) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const at = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    onSeek(at * length);
  };
  const stopDrag = () => {
    dragging.current = false;
  };
  return {
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      dragging.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      seekTo(e.clientX);
    },
    onPointerMove: (e: PointerEvent<HTMLDivElement>) => {
      if (dragging.current) seekTo(e.clientX);
    },
    onPointerUp: stopDrag,
    onPointerCancel: stopDrag,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      const to = {
        ArrowRight: currentTime + STEP,
        ArrowUp: currentTime + STEP,
        ArrowLeft: currentTime - STEP,
        ArrowDown: currentTime - STEP,
        Home: 0,
        End: length,
      }[e.key];
      if (to === undefined) return;
      e.preventDefault();
      onSeek(Math.min(length, Math.max(0, to)));
    },
  };
}
